import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Target, RefreshCw, Building2 } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

function fmt$(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Coverage pill — visually distinguishes "no data ever" from "partial coverage"
// from "full coverage" without the admin having to eyeball the by_tech map.
function coverageTone(techsWithAny, techsWithoutAny) {
  if (techsWithAny === 0) return "bg-[#fdecec] text-[#a12b2b] border-[#f5c5c5]";
  if (techsWithoutAny === 0) return "bg-[#e6f9f2] text-[#0e5a48] border-[#c1eddc]";
  return "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]";
}

export default function AdminTechGoalsAudit() {
  const [tenantSlug, setTenantSlug] = useState("mre-swmo");
  const [tenants, setTenants] = useState([]);
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [refreshing, setRefreshing] = useState(false);

  const loadTenants = useCallback(async () => {
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch {
      // Best-effort — the tenant picker degrades to the raw text input
      // if /tenants isn't available for this admin. Not blocking.
    }
  }, []);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoadState({ status: "loading", error: null });
    try {
      const { data } = await api.get(`/admin/tech-goals-audit?tenant_slug=${encodeURIComponent(tenantSlug)}`);
      setData(data);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setData(null);
      setLoadState({ status: "error", error: extractApiError(err) });
    } finally {
      setRefreshing(false);
    }
  }, [tenantSlug]);

  useEffect(() => { loadTenants(); }, [loadTenants]);
  useEffect(() => { load(); }, [load]);

  if (loadState.status === "loading" && !data) {
    return (
      <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-tech-goals-audit-page">
        <Header />
        <PanelState state="loading" />
      </div>
    );
  }
  if (loadState.status === "error") {
    return (
      <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-tech-goals-audit-page">
        <Header />
        <PanelState state="error" error={loadState.error} onRetry={() => load()} />
      </div>
    );
  }

  const byTech = data?.by_tech || {};
  const techNames = Object.keys(byTech).sort();
  const techsWithAny = techNames.filter((t) => byTech[t].count > 0).length;
  const techsWithoutAny = techNames.filter((t) => byTech[t].count === 0).length;
  const weeks = data?.weeks_with_goals || [];

  return (
    <div className="max-w-7xl mx-auto space-y-5" data-testid="admin-tech-goals-audit-page">
      <Header />

      {/* Tenant picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12.5px] text-[#5d6a80]">
          <Building2 size={13} strokeWidth={1.8} />
          {tenants.length > 0 ? (
            <select
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="border border-[#dde3ec] rounded px-2 py-1 bg-white text-[#1e2c48]"
              data-testid="tenant-picker"
            >
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>{t.business_name} ({t.slug})</option>
              ))}
            </select>
          ) : (
            <input
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="border border-[#dde3ec] rounded px-2 py-1 bg-white text-[#1e2c48] font-mono-data text-[12px]"
              placeholder="tenant slug"
              data-testid="tenant-picker-input"
            />
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="p-1.5 rounded-md hover:bg-[#f5f6f8] disabled:opacity-50"
          data-testid="tech-goals-audit-refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={14} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Summary banner */}
      <div
        className={`border rounded-lg p-3.5 ${coverageTone(techsWithAny, techsWithoutAny)}`}
        data-testid="tech-goals-audit-banner"
      >
        <div className="text-[13px] font-semibold flex items-center gap-2 flex-wrap">
          <Target size={16} strokeWidth={1.8} />
          Weekly Goal coverage — {data?.tenant_slug}
        </div>
        <div className="text-[11.5px] font-mono-data mt-1 opacity-90 flex flex-wrap gap-x-4 gap-y-1">
          <span>total_rows={data?.total_rows ?? 0}</span>
          <span>weeks_covered={weeks.length}</span>
          <span>techs_with_goals={techsWithAny}</span>
          <span>techs_without_goals={techsWithoutAny}</span>
        </div>
        <div className="text-[11.5px] mt-1.5 opacity-90" data-testid="tech-goals-audit-diagnosis">
          {data?.diagnosis}
        </div>
      </div>

      {/* Weeks covered */}
      {weeks.length > 0 && (
        <div className="border border-[#e7e9ee] rounded-lg bg-white p-3.5" data-testid="tech-goals-audit-weeks">
          <div className="text-[10.5px] uppercase tracking-widest text-[#8090aa] font-semibold mb-2">
            Weeks with at least one goal entered
          </div>
          <div className="flex flex-wrap gap-1.5">
            {weeks.map((w) => (
              <span
                key={w}
                className="text-[11.5px] font-mono-data px-1.5 py-0.5 rounded bg-[#eef1f6] text-[#33445e] border border-[#dde3ec]"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Per-tech table */}
      {techNames.length === 0 ? (
        <div className="border border-dashed border-[#dde3ec] rounded-lg bg-[#fafbfc] p-10 text-center"
          data-testid="tech-goals-audit-empty">
          <Target size={28} strokeWidth={1.5} className="mx-auto text-[#8090aa] mb-3" />
          <div className="text-[14px] font-semibold text-[#1e2c48]">No techs found for this tenant</div>
          <div className="text-[12.5px] text-[#5d6a80] mt-1">
            Neither kpi_jobs nor tech_goals contain any rows tagged to this tenant.
          </div>
        </div>
      ) : (
        <div className="border border-[#e7e9ee] rounded-lg bg-white overflow-hidden" data-testid="tech-goals-by-tech-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#fafbfc] border-b border-[#e7e9ee]">
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#8090aa]">
                <th className="px-4 py-2.5 font-semibold">Technician</th>
                <th className="px-4 py-2.5 font-semibold text-right">Goal Rows</th>
                <th className="px-4 py-2.5 font-semibold">Most Recent Week</th>
                <th className="px-4 py-2.5 font-semibold text-right">Most Recent Goal</th>
                <th className="px-4 py-2.5 font-semibold text-right">Avg Goal</th>
              </tr>
            </thead>
            <tbody>
              {techNames.map((tech) => {
                const row = byTech[tech];
                const hasAny = row.count > 0;
                return (
                  <tr
                    key={tech}
                    className={`border-b border-[#eef1f6] last:border-0 hover:bg-[#fafbfc] ${hasAny ? "" : "opacity-70"}`}
                    data-testid={`tech-goal-row-${tech}`}
                  >
                    <td className="px-4 py-3 font-medium text-[#0e1420]">{tech}</td>
                    <td className="px-4 py-3 text-right font-mono-data">
                      {hasAny ? row.count : <span className="text-[#a12b2b]">0</span>}
                    </td>
                    <td className="px-4 py-3 text-[#5d6a80] font-mono-data text-[11.5px]">
                      {row.most_recent_week || <span className="italic">never</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-data font-semibold">
                      {fmt$(row.most_recent_amount)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-data text-[#5d6a80]">
                      {fmt$(row.avg)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Header() {
  return (
    <div className="space-y-1">
      <div className="text-[11.5px] uppercase tracking-widest text-[#8090aa] font-semibold">
        Platform Admin
      </div>
      <h1 className="text-2xl font-semibold text-[#1e2c48] flex items-center gap-2">
        <Target size={22} strokeWidth={1.8} className="text-[#8a5b00]" />
        Tech Goals Audit
      </h1>
      <p className="text-[13px] text-[#5d6a80]">
        Read-only audit of the <code className="font-mono-data text-[11.5px] bg-[#f5f6f8] px-1 rounded">tech_goals</code> collection.
        Confirms whether Scorecard &quot;Weekly Goal: not set&quot; reflects genuine missing data or a carry-forward misfire.
      </p>
    </div>
  );
}
